import { useEffect, useRef, useState } from 'react';
import { formatCep } from '../lib/billing/brIdentity';
import { lookupCep } from '../lib/billing/viaCep';
import FieldVerifiedIcon from './FieldVerifiedIcon';

export type BillingAddressValue = {
  postalCode: string;
  addressNumber: string;
  street: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
};

type Props = {
  value: BillingAddressValue;
  onChange: (next: BillingAddressValue) => void;
};

export default function BillingAddressFields({ value, onChange }: Props) {
  const [cepStatus, setCepStatus] = useState<'idle' | 'loading' | 'found' | 'miss'>('idle');
  const lastLookup = useRef('');
  const valueRef = useRef(value);
  valueRef.current = value;
  const postalDigits = value.postalCode.replace(/\D/g, '');

  useEffect(() => {
    if (postalDigits.length !== 8) {
      setCepStatus('idle');
      lastLookup.current = '';
      return;
    }
    if (lastLookup.current === postalDigits) return;
    lastLookup.current = postalDigits;
    let cancelled = false;
    setCepStatus('loading');
    (async () => {
      try {
        const found = await lookupCep(postalDigits);
        if (cancelled) return;
        if (!found) {
          setCepStatus('miss');
          return;
        }
        setCepStatus('found');
        const current = valueRef.current;
        onChange({
          ...current,
          postalCode: formatCep(postalDigits),
          street: found.street || current.street,
          neighborhood: found.neighborhood || current.neighborhood,
          city: found.city,
          state: found.state,
        });
      } catch {
        if (!cancelled) setCepStatus('miss');
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only re-run when the CEP digits change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalDigits]);

  const patch = (partial: Partial<BillingAddressValue>) => onChange({ ...value, ...partial });
  const cepReady = cepStatus === 'found' || (postalDigits.length === 8 && !!value.city && value.state.length === 2);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="my-label" htmlFor="postalCode">
            CEP
          </label>
          <div className="relative">
            <input
              id="postalCode"
              className="my-input pr-11"
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="00000-000"
              value={value.postalCode}
              onChange={(e) => {
                lastLookup.current = '';
                patch({ postalCode: formatCep(e.target.value) });
              }}
            />
            {cepReady ? <FieldVerifiedIcon label="CEP encontrado" /> : null}
          </div>
          {cepStatus === 'loading' ? (
            <p className="mt-1 text-xs text-muted-foreground">Buscando endereço…</p>
          ) : null}
          {cepStatus === 'miss' ? (
            <p className="mt-1 text-xs text-muted-foreground">
              CEP não encontrado. Preencha o endereço.
            </p>
          ) : null}
        </div>
        <div>
          <label className="my-label" htmlFor="addressNumber">
            Número
          </label>
          <input
            id="addressNumber"
            className="my-input"
            autoComplete="off"
            value={value.addressNumber}
            onChange={(e) => patch({ addressNumber: e.target.value })}
          />
        </div>
      </div>

      <div>
        <label className="my-label" htmlFor="street">
          Rua / logradouro
        </label>
        <input
          id="street"
          className="my-input"
          autoComplete="street-address"
          value={value.street}
          onChange={(e) => patch({ street: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="my-label" htmlFor="complement">
            Complemento
          </label>
          <input
            id="complement"
            className="my-input"
            autoComplete="address-line2"
            placeholder="Apto, sala…"
            value={value.complement}
            onChange={(e) => patch({ complement: e.target.value })}
          />
        </div>
        <div>
          <label className="my-label" htmlFor="neighborhood">
            Bairro
          </label>
          <input
            id="neighborhood"
            className="my-input"
            autoComplete="address-level3"
            value={value.neighborhood}
            onChange={(e) => patch({ neighborhood: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-[1fr_5.5rem] gap-3">
        <div>
          <label className="my-label" htmlFor="city">
            Cidade
          </label>
          <input
            id="city"
            className="my-input"
            autoComplete="address-level2"
            value={value.city}
            onChange={(e) => patch({ city: e.target.value })}
          />
        </div>
        <div>
          <label className="my-label" htmlFor="state">
            UF
          </label>
          <input
            id="state"
            className="my-input uppercase"
            autoComplete="address-level1"
            maxLength={2}
            value={value.state}
            onChange={(e) => patch({ state: e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase() })}
          />
        </div>
      </div>
    </div>
  );
}
